/**
 * UI plugins: the half of a plugin that lives in the window.
 *
 * An engine plugin may ship `ui.js` beside its Python module. When the plugin
 * is loaded, the engine lists it at `/api/plugins` with a `ui` URL, and this
 * module imports that file and calls its default export with the API below:
 *
 *     export default function activate(nova) {
 *       nova.views.add({ id: "hello", label: "Hello", icon: "plug", section: "plugins",
 *                        render: () => nova.h("div", null, "Hello from a plugin") });
 *       nova.trackActions.add({ id: "hello.say", label: "Say hello", group: "plugins",
 *                               run: (tracks) => nova.ui.toast(`${tracks.length} selected`) });
 *       nova.events.on("library.changed", () => …);
 *       const data = await nova.engine.get("/api/plugins/hello/greeting");
 *     }
 *
 * Everything a plugin adds is owned by it: disabling the plugin removes its
 * views, menu items, actions, settings pages, layouts, status items and bus
 * listeners in one go. `nova.h` is React's createElement, so a plugin needs no
 * build step; a plugin that bundles its own React must still render through
 * the `h` it is given.
 *
 * **Bundled plugins** ship with Nova and are compiled into the app instead of
 * served as `ui.js`: BUNDLED_UI maps the engine plugin's id to its module,
 * fetched only when the engine says that plugin is on. Radio is one.
 */

import { lockLayout } from "./layoutLock";
import { createElement, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { engineBase } from "../api";
import { withToken } from "./session";
import { bus } from "./bus";
import { player } from "./player";
import { registry, type LayoutContribution, type MenuId, type MenuItem, type SettingsPage, type StatusItem, type TrackAction, type UserTool, type ViewContribution } from "./registry";
import { toast } from "../shell/Toast";
import * as kit from "./pluginKit";

/** Added members do not bump this (docs/PLUGINS.md); `kit` came in 2026-10-27. */
export const UI_API_VERSION = 1;

export interface EnginePlugin {
  id: string;
  name: string;
  version: string;
  description: string;
  source: string;
  enabled: boolean;
  loaded: boolean;
  error: string | null;
  ui: string | null;
  /** Off until the user turns it on (first-run setup or the Plugin manager). */
  optional?: boolean;
  /** An icon name from Nova's set, for the setup and the Plugin manager. */
  icon?: string | null;
}

type Activate = (api: NovaUI) => unknown;

/** Window halves compiled into Nova, by engine plugin id. Loaded on demand. */
const BUNDLED_UI: Record<string, () => Promise<{ default: Activate }>> = {
  radio: () => import("../plugins/radio"),
};

/** What `activate(nova)` receives. Stable across minor versions of Nova. */
export function apiFor(owner: string) {
  const call = async (method: "GET" | "POST", path: string, body?: unknown) => {
    const res = await fetch(`${engineBase()}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: method === "POST" ? JSON.stringify(body ?? {}) : undefined,
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  };
  return {
    version: UI_API_VERSION,
    id: owner,
    h: createElement,
    Fragment,
    /** React's hooks, so a plugin screen can keep state without bundling React. */
    react: { useState, useEffect, useMemo, useRef, useCallback },
    views: { add: (v: ViewContribution) => registry.addView(v, owner) },
    menus: { add: (menu: MenuId, item: MenuItem) => registry.addMenuItem(menu, item, owner) },
    trackActions: { add: (a: TrackAction) => registry.addTrackAction(a, owner) },
    settings: { add: (p: SettingsPage) => registry.addSettingsPage(p, owner) },
    layouts: {
      add: (l: LayoutContribution) => registry.addLayout(l, owner),
      /** Show this layout whatever was chosen, in this window, without the switcher; null unlocks. */
      lock: (id: string | null) => lockLayout(id, owner),
    },
    status: { add: (s: StatusItem) => registry.addStatusItem(s, owner) },
    /** Something added to each person in Users & roles (a button and what it opens). */
    users: { add: (t: UserTool) => registry.addUserTool(t, owner) },
    events: {
      on: (pattern: string, fn: Parameters<typeof bus.on>[1]) => bus.on(pattern, fn, owner),
      emit: (topic: string, data?: unknown) => bus.emit(`plugins.${owner}.${topic}`, data, owner),
    },
    engine: {
      url: (path: string) => `${engineBase()}${path}`,
      get: (path: string) => call("GET", path),
      post: (path: string, body?: unknown) => call("POST", path, body),
    },
    player: {
      play: player.playTracks.bind(player),
      enqueue: player.enqueue.bind(player),
      playNext: player.playNext.bind(player),
      toggle: player.toggle.bind(player),
      next: () => player.next(),
      prev: () => player.prev(),
      state: player.get,
      subscribe: player.subscribe,
    },
    ui: { toast },
    /** What a plugin builds its screens from: players, prefs, the bus, Nova's own components. */
    kit,
  };
}

export type NovaUI = ReturnType<typeof apiFor>;

const active = new Map<string, { dispose?: () => void }>();
let settled = false;

/**
 * Whether the window has asked the engine for its plugins at least once. A
 * layout a plugin adds (Radio) is missing until then; the shell waits for this
 * rather than flash another layout first.
 */
export const pluginsSettled = () => settled;

export async function fetchPlugins(): Promise<EnginePlugin[]> {
  try {
    const res = await fetch(`${engineBase()}/api/plugins`);
    const body = (await res.json()) as { plugins?: EnginePlugin[] };
    return body.plugins ?? [];
  } catch {
    return [];
  }
}

let queue: Promise<unknown> = Promise.resolve();

/** Bring the window's plugins in line with the engine's list. One sync at a
 *  time, so two quick engine events cannot activate a plugin twice. */
export function syncPlugins(): Promise<EnginePlugin[]> {
  const next = queue.then(syncOnce, syncOnce);
  queue = next.catch(() => undefined);
  return next;
}

async function syncOnce(): Promise<EnginePlugin[]> {
  const list = await fetchPlugins();
  const wanted = new Map(list.filter((p) => p.loaded && (p.ui || BUNDLED_UI[p.id])).map((p) => [p.id, p]));
  for (const id of [...active.keys()]) if (!wanted.has(id)) unloadPlugin(id);
  for (const p of wanted.values()) {
    if (active.has(p.id)) continue;
    try {
      const mod = (BUNDLED_UI[p.id]
        ? await BUNDLED_UI[p.id]()
        : await import(/* @vite-ignore */ withToken(`${engineBase()}${p.ui}?v=${encodeURIComponent(p.version)}`))) as {
        default?: Activate;
      };
      if (typeof mod.default !== "function") throw new Error("ui.js has no default export function");
      registry.setOwnerName(p.id, p.name);
      const result = await mod.default(apiFor(p.id));
      active.set(p.id, { dispose: typeof result === "function" ? (result as () => void) : undefined });
      bus.emit("plugins.ui.loaded", { id: p.id });
    } catch (err) {
      console.error(`[nova] UI for plugin ${p.id} failed`, err);
      unloadPlugin(p.id);
      toast(`Plugin “${p.name}” could not load its interface: ${err instanceof Error ? err.message : String(err)}`, "bad");
    }
  }
  settled = true;
  bus.emit("plugins.ui.synced", { ids: [...active.keys()] });
  return list;
}

export function unloadPlugin(id: string) {
  const entry = active.get(id);
  try {
    entry?.dispose?.();
  } catch (err) {
    console.error(err);
  }
  active.delete(id);
  registry.removeOwner(id);
  lockLayout(null, id); // a plugin turned off lets go of the layout it locked
  bus.offOwner(id);
}

export async function setPluginEnabled(id: string, enabled: boolean): Promise<string | null> {
  try {
    const res = await fetch(`${engineBase()}/api/plugins/enable`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, enabled }),
    });
    const body = (await res.json()) as { error?: string | null };
    await syncPlugins();
    return body.error ?? null;
  } catch (err) {
    return err instanceof Error ? err.message : String(err);
  }
}
